import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nodeAxiosFirebase, {
  throwIfErrorData,
} from '../utils/nodeAxiosFirebase';
import moment from 'moment';
import { setIsLoadingSection } from './coreSlice';

export const MAX_CACHED_MODULES = 6;
// Only inactive lists count toward this budget. Never truncate a visible list.
export const MAX_CACHED_INACTIVE_ROWS = 2000;

const RESERVED_LIST_KEYS = new Set([
  'employees',
  'facets',
  'moduleAccessOrder',
  'lastEvicted',
]);

const getOrCreateStructure = (state, structureId) => {
  if (!structureId) {
    return null;
  }
  if (!state[structureId]) {
    state[structureId] = {
      structureId: null,
      moduleId: null,
      data: {},
      collectionData: {},
      lastDoc: undefined,
      loading: false,
      error: null,
      selectedDate: null,
      selectedRangeEnd: null,
      selectedView: 'none',
      searchValue: '',
      sortBy: null,
      sortOrder: null,
      lastUpdate: null,
      hasActiveFilters: false,
      kanbanCollapsed: false,
      selectedItems: [],
      quickFilters: { facets: [], numerics: [] },
      taskScope: 'mine',
      sourceCurrency: null,
      activeTab: 0,
    };
  }
  return state[structureId];
};

const touchAccessOrder = (state, stateKey) => {
  if (!stateKey || RESERVED_LIST_KEYS.has(stateKey)) return;
  const order = state.moduleAccessOrder || [];
  state.moduleAccessOrder = [
    stateKey,
    ...order.filter((key) => key !== stateKey),
  ];
};

const toUnixSeconds = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (value instanceof Date) {
    return Math.floor(value.getTime() / 1000);
  }
  if (moment(value).isValid()) {
    return moment(value).unix();
  }
  return null;
};

export const fetchFacetsData = createAsyncThunk(
  'list/fetchFacetsData',
  async (
    { structureId, moduleId, lang, activeTab, taskScope },
    { dispatch, getState }
  ) => {
    try {
      dispatch(
        setLoading({ structureId: `${structureId}_facets`, loading: true })
      );

      const listStateKey = moduleId || structureId;
      const resolvedTaskScope =
        taskScope || getState().list[listStateKey]?.taskScope;

      const body = {
        lang: lang?.slice(0, 2) || 'fr',
        moduleId: moduleId,
        structureId: structureId,
        activeTab: activeTab,
        query: '',
      };

      if (resolvedTaskScope) {
        body.taskScope = resolvedTaskScope;
      }

      const response = throwIfErrorData(
        await nodeAxiosFirebase({
          method: 'POST',
          url: 'bosV4/list',
          body: body,
        })
      );

      const facetsData = {
        structureId,
        moduleName: body.moduleName,
        facets: response?.facets || {},
        queryID: response?.queryID,
      };

      dispatch(setFacets(facetsData));
      dispatch(
        setLoading({ structureId: `${structureId}_facets`, loading: false })
      );

      return facetsData;
    } catch (error) {
      dispatch(
        setLoading({ structureId: `${structureId}_facets`, loading: false })
      );
      dispatch(
        setError({ structureId: `${structureId}_facets`, error: error.message })
      );
      return Promise.reject(error);
    }
  }
);

export const fetchListData = createAsyncThunk(
  'list/fetchListData',
  async (
    {
      structureId,
      moduleId,
      lang,
      activeTab,
      searchValue,
      page,
      numericFilters,
      filterFacets,
      orderBy,
      order,
      segmentId,
      startAfter,
    },
    { dispatch, getState }
  ) => {
    // Utiliser moduleId comme clé pour éviter les collisions entre modules
    const stateKey = moduleId || structureId;

    try {
      dispatch(setLoading({ structureId: stateKey, loading: true }));
      let body = {
        lang: lang?.slice(0, 2) || 'fr',
        moduleId: moduleId,
        structureId: structureId,
        activeTab: activeTab,
      };
      const sourceCurrency = getState().list[stateKey]?.sourceCurrency;
      if (sourceCurrency) body.currency = sourceCurrency;

      if (searchValue !== null && searchValue !== undefined) {
        body = {
          ...body,
          query: searchValue,
        };
      }

      if (numericFilters?.length > 0) {
        body.numericFilters = numericFilters;
      }
      if (filterFacets) {
        body.filterFacets = filterFacets;
      }
      if (orderBy) {
        body.orderBy = orderBy;
      }
      if (order) {
        body.order = order;
      }
      if (segmentId) {
        body.segmentId = segmentId;
      }
      if (startAfter) {
        body.startAfter = startAfter;
      }

      const response = throwIfErrorData(
        await nodeAxiosFirebase({
          method: 'POST',
          url: 'bosV4/list',
          body: body,
        })
      );

      const data = response?.hits?.map((doc) => ({
        ...doc,
        path:
          response?.collection === 'connections'
            ? doc?.documentPath || doc?.path
            : response?.collection + '/' + doc?.id,
        id: doc?.id,
      }));

      if (data?.length > 0) {
        const lastVisible = response?.lastVisible;
        dispatch(setLastDoc({ structureId: stateKey, lastDoc: lastVisible }));
      } else {
        dispatch(setLastDoc({ structureId: stateKey, lastDoc: undefined }));
      }
      dispatch(setIsLoadingSection(null));
      dispatch(
        fetchDataSuccess({
          structureId: stateKey,
          data,
          collectionField: response?.collectionField,
        })
      );

      const objectIDsDoc = data?.map((doc) => doc.id);
      const firstTen = objectIDsDoc?.slice(0, 10);
      const hitsTotal = response?.totalHits;

      const collectionDataResponse = {
        lang: body.lang,
        name: response?.name || response?.collectionField,
        nbHits: response?.nbHits || 0,
        nbDocuments: response?.hits?.length || 0,
        totalDocuments: hitsTotal,
        facets: response?.facets,
        source: response?.source,
        activeCollection: response?.collectionField,
        collection: response?.collectionField,
        structureId: structureId,
        hasLayout: response?.hasLayout ?? true,
        header: response?.header,
        summary: response?.summary,
        currentPage: page,
        queryID: response?.queryID,
        objectIDs: firstTen,
        tabs: response?.tabs ?? response?.preferences?.tabs,
        edit: response?.edit ?? response?.preferences?.edit ?? false,
        email: response?.email ?? response?.preferences?.email ?? false,
      };

      dispatch(
        setCurrentCollection({
          structureId: stateKey,
          collectionData: collectionDataResponse,
          collectionField: response?.collectionField,
        })
      );

      dispatch(setLoading({ structureId: stateKey, loading: false }));
      return { structureId: stateKey, moduleId, data };
    } catch (error) {
      dispatch(setLoading({ structureId: stateKey, loading: false }));
      dispatch(setError({ structureId: stateKey, error: error.message }));
      return Promise.reject(error);
    }
  }
);

export const fetchEmployeesList = createAsyncThunk(
  'list/fetchEmployeesList',
  async ({ querySearch }, { dispatch, getState }) => {
    try {
      dispatch(setLoading({ structureId: 'employees', loading: true }));

      const body = {
        querySearch: querySearch,
        limit: 8,
      };

      const response = await nodeAxiosFirebase({
        method: 'POST',
        url: 'main/employees',
        body: body,
      });

      dispatch(setEmployees(response));
      dispatch(setLoading({ structureId: 'employees', loading: false }));
      return { structureId: 'employees', response };
    } catch (error) {
      dispatch(setLoading({ structureId: 'employees', loading: false }));
      dispatch(setError({ structureId: 'employees', error: error.message }));
      return Promise.reject(error);
    }
  }
);

export const createEasyAddItem = createAsyncThunk(
  'list/createEasyAddItem',
  async (
    { structureId, moduleId, item, lang },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await nodeAxiosFirebase({
        method: 'POST',
        url: 'coreSeqV6/single',
        body: {
          structureId,
          moduleId,
          document: {
            values: item.values,
            completed: item.completed,
            order: item.order,
          },
          lang: lang?.slice(0, 2) || 'fr',
        },
      });

      // Update the local state with the created item
      dispatch(
        addEasyAddItem({
          structureId,
          collectionField: response.collectionField,
          item: { ...item, id: response.id },
        })
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEasyAddItemOrder = createAsyncThunk(
  'list/updateEasyAddItemOrder',
  async (
    { structureId, moduleId, items, lang },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Single coreSeqV6 bulk-edit job instead of N parallel PATCHes: avoids
      // queuing N sequential per-business jobs (bulk-edit applies them all
      // within one job/lock acquisition instead).
      const edits = items.map((item, index) => ({
        documentId: item.id,
        elementPath: `${moduleId}/${structureId}/${item.id}`,
        value: index,
        key: 'order',
      }));

      // coreSeqV6/bulk-edit caps at 50 edits per request; chunk larger reorders.
      const BULK_EDIT_CHUNK_SIZE = 50;
      for (let i = 0; i < edits.length; i += BULK_EDIT_CHUNK_SIZE) {
        await nodeAxiosFirebase({
          method: 'PATCH',
          url: 'coreSeqV6/bulk-edit',
          body: { edits: edits.slice(i, i + BULK_EDIT_CHUNK_SIZE) },
        });
      }

      dispatch(
        reorderEasyAddItems({
          structureId,
          collectionField: items[0]?.collectionField,
          draggedItemId: items[0]?.id,
          targetItemId: items[items.length - 1]?.id,
        })
      );

      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  employees: {
    data: [],
    loading: false,
    error: null,
  },
  facets: {},
  moduleAccessOrder: [],
  lastEvicted: [],
};

const listSlice = createSlice({
  name: 'list',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      const { structureId, loading } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.loading = loading;
    },
    setError: (state, action) => {
      const { structureId, error } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.error = error;
    },
    fetchDataSuccess: (state, action) => {
      const {
        structureId,
        data,
        collectionField,
        realStructureId = null,
        moduleId = null,
      } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (realStructureId) structureState.structureId = realStructureId;
      if (moduleId) structureState.moduleId = moduleId;
      if (!collectionField) return;
      structureState.data[collectionField] = Array.isArray(data) ? data : [];
      structureState.error = null;
      touchAccessOrder(state, structureId);
    },
    appendListData: (state, action) => {
      const { structureId, data, collectionField } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (structureState.data[collectionField]) {
        // A derived source may expose several currency rows for one document.
        const existingIds = new Set(
          structureState.data[collectionField].map((item) => item.rowKey || item.id)
        );
        const newItems = data.filter((item) => !existingIds.has(item.rowKey || item.id));
        structureState.data[collectionField] = [
          ...structureState.data[collectionField],
          ...newItems,
        ];
      } else {
        structureState.data[collectionField] = data;
      }
      touchAccessOrder(state, structureId);
    },
    mergeListData: (state, action) => {
      const { structureId, data, collectionField } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (!data || !Array.isArray(data)) return;

      if (structureState.data[collectionField]) {
        // Create a map of new data by id for quick lookup
        const newDataMap = new Map(data.map((item) => [item.rowKey || item.id, item]));

        // Update existing items and keep items that weren't in the new data
        const mergedData = structureState.data[collectionField].map((existingItem) => {
          const newItem = newDataMap.get(existingItem.rowKey || existingItem.id);
          if (newItem) {
            // Item exists in both - merge/update with new data
            newDataMap.delete(existingItem.rowKey || existingItem.id);
            return { ...existingItem, ...newItem };
          }
          // Item only in existing data - keep it
          return existingItem;
        });

        // Add any new items that weren't in existing data (at the beginning)
        const newItems = Array.from(newDataMap.values());
        structureState.data[collectionField] = [...newItems, ...mergedData];
      } else {
        structureState.data[collectionField] = data;
      }
      touchAccessOrder(state, structureId);
    },
    resetListData: (state, action) => {
      const { structureId, collectionField } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (collectionField) {
        structureState.data[collectionField] = [];
      } else {
        structureState.data = {};
      }
    },
    touchModuleAccess: (state, action) => {
      const { stateKey, structureId = null, moduleId = null } = action.payload;
      const structureState = getOrCreateStructure(state, stateKey);
      if (!structureState) return;
      if (structureId) structureState.structureId = structureId;
      if (moduleId) structureState.moduleId = moduleId;
      touchAccessOrder(state, stateKey);
    },
    evictInactiveModules: (state, action) => {
      const {
        activeKey = null,
        maxModules = MAX_CACHED_MODULES,
        maxInactiveRows = MAX_CACHED_INACTIVE_ROWS,
        protectedKeys = [],
      } = action.payload || {};
      const order = state.moduleAccessOrder || [];
      const keysToKeep = new Set(protectedKeys);
      if (activeKey) keysToKeep.add(activeKey);
      // Legacy list requests and realtime refreshes also write to Redux.
      order.forEach((key) => {
        if (state[key]?.loading) keysToKeep.add(key);
      });
      let retainedModules = order.filter((key) => keysToKeep.has(key)).length;
      let retainedRows = 0;

      const evicted = [];
      for (const key of order) {
        if (keysToKeep.has(key) || RESERVED_LIST_KEYS.has(key)) continue;
        const entry = state[key];
        if (!entry || typeof entry !== 'object') continue;

        const hasCachedData =
          (entry.data && Object.keys(entry.data).length > 0) ||
          (entry.collectionData && Object.keys(entry.collectionData).length > 0) ||
          Boolean(entry.lastDoc);
        if (!hasCachedData) continue;

        const rowCount = Object.values(entry.data || {}).reduce(
          (count, rows) => count + (Array.isArray(rows) ? rows.length : 0),
          0
        );
        if (
          retainedModules < maxModules &&
          retainedRows + rowCount <= maxInactiveRows
        ) {
          retainedModules += 1;
          retainedRows += rowCount;
          continue;
        }

        evicted.push({
          stateKey: key,
          structureId: entry.structureId || null,
          moduleId: entry.moduleId || key,
        });
        // Evict refetchable cache only — keep UI preferences intact
        entry.data = {};
        entry.collectionData = {};
        entry.lastDoc = undefined;
      }
      state.lastEvicted = evicted;
    },
    setEmployees: (state, action) => {
      state.employees.data = action.payload;
    },
    setFacets: (state, action) => {
      const { structureId, facets } = action.payload;
      state.facets[structureId] = facets;
    },
    setLastDoc: (state, action) => {
      const { structureId, lastDoc } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.lastDoc = lastDoc;
    },
    setCurrentCollection: (state, action) => {
      const { structureId, collectionData, collectionField } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (
        collectionData.summary === undefined &&
        structureState.collectionData[collectionField]?.summary
      ) {
        collectionData.summary =
          structureState.collectionData[collectionField].summary;
      }
      structureState.collectionData[collectionField] = collectionData;
    },
    setSelectedDate: (state, action) => {
      const {
        structureId,
        selectedDate,
        selectedRangeEnd = null,
      } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;

      structureState.selectedDate = toUnixSeconds(selectedDate);
      structureState.selectedRangeEnd = toUnixSeconds(selectedRangeEnd);
    },
    setSelectedView: (state, action) => {
      const { structureId, selectedView } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.selectedView = selectedView;
    },
    setSearchValue: (state, action) => {
      const { structureId, searchValue } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.searchValue = searchValue || '';
    },
    setSortOrder: (state, action) => {
      const { structureId, sortBy, sortOrder } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.sortBy = sortBy;
      structureState.sortOrder = sortOrder;
    },
    setSourceCurrency: (state, action) => {
      const { structureId, currency } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.sourceCurrency = currency || null;
      structureState.data = {};
      structureState.lastDoc = undefined;
      structureState.selectedItems = [];
      if (currency === 'all') {
        // Amounts from different currencies have no common ordering.
        structureState.sortBy = 'name';
        structureState.sortOrder = 'asc';
        structureState.quickFilters = { facets: [], numerics: [] };
      }
    },
    setLastUpdate: (state, action) => {
      const { structureId, lastUpdate } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.lastUpdate = lastUpdate;
    },
    setHasActiveFilters: (state, action) => {
      const { structureId, hasActiveFilters } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.hasActiveFilters = hasActiveFilters;
    },
    setKanbanCollapsed: (state, action) => {
      const { structureId, kanbanCollapsed } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.kanbanCollapsed = kanbanCollapsed;
    },
    setTaskScope: (state, action) => {
      const { structureId, taskScope } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.taskScope = taskScope;
    },
    setActiveTab: (state, action) => {
      const { structureId, activeTab } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.activeTab = activeTab;
      structureState.sourceCurrency = null;
    },
    setQuickFilters: (state, action) => {
      const { structureId, facets = [], numerics = [] } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.quickFilters = { facets, numerics };
    },
    addEasyAddItem: (state, action) => {
      const { structureId, collectionField, item } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (structureState.data[collectionField]) {
        structureState.data[collectionField].push(item);
      } else {
        structureState.data[collectionField] = [item];
      }
    },
    updateEasyAddItem: (state, action) => {
      const { structureId, collectionField, itemId, updates } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (structureState.data[collectionField]) {
        const itemIndex = structureState.data[collectionField].findIndex(
          (item) => item.id === itemId
        );
        if (itemIndex !== -1) {
          structureState.data[collectionField][itemIndex] = {
            ...structureState.data[collectionField][itemIndex],
            ...updates,
          };
        }
      }
    },
    reorderEasyAddItems: (state, action) => {
      const { structureId, collectionField, draggedItemId, targetItemId } =
        action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (structureState.data[collectionField]) {
        const items = [...structureState.data[collectionField]];
        const draggedIndex = items.findIndex(
          (item) => item.id === draggedItemId
        );
        const targetIndex = items.findIndex((item) => item.id === targetItemId);

        if (draggedIndex !== -1 && targetIndex !== -1) {
          const [draggedItem] = items.splice(draggedIndex, 1);
          items.splice(targetIndex, 0, draggedItem);

          // Update order property for all items
          items.forEach((item, index) => {
            item.order = index;
          });

          structureState.data[collectionField] = items;
        }
      }
    },
    toggleEasyAddItemComplete: (state, action) => {
      const { structureId, collectionField, itemId } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      if (structureState.data[collectionField]) {
        const itemIndex = structureState.data[collectionField].findIndex(
          (item) => item.id === itemId
        );
        if (itemIndex !== -1) {
          structureState.data[collectionField][itemIndex].completed =
            !structureState.data[collectionField][itemIndex].completed;
        }
      }
    },
    toggleSelectItem: (state, action) => {
      const { structureId, item } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      const idx = structureState.selectedItems.findIndex((i) => i.id === item.id);
      if (idx >= 0) {
        structureState.selectedItems.splice(idx, 1);
      } else {
        structureState.selectedItems.push(item);
      }
    },
    selectAllItems: (state, action) => {
      const { structureId, items } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.selectedItems = items;
    },
    removeListItems: (state, action) => {
      const { structureId, collectionField, ids } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState || !structureState.data[collectionField]) return;
      const idsSet = new Set(ids);
      structureState.data[collectionField] = structureState.data[collectionField].filter(
        (item) => !idsSet.has(item.id) && !idsSet.has(item.objectID)
      );
    },
    clearSelectedItems: (state, action) => {
      const { structureId } = action.payload;
      const structureState = getOrCreateStructure(state, structureId);
      if (!structureState) return;
      structureState.selectedItems = [];
    },
    clearList: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchListData.pending, (state, action) => {
        const { structureId } = action.meta.arg;
        if (structureId) {
          const structureState = getOrCreateStructure(state, structureId);
          if (!structureState) return;
          structureState.loading = true;
          structureState.error = null;
        }
      })
      .addCase(fetchListData.fulfilled, (state, action) => {
        const { structureId } = action.payload;
        if (structureId) {
          const structureState = getOrCreateStructure(state, structureId);
          if (!structureState) return;
          structureState.loading = false;
        }
      })
      .addCase(fetchListData.rejected, (state, action) => {
        const { structureId } = action.meta.arg;
        if (structureId) {
          const structureState = getOrCreateStructure(state, structureId);
          if (!structureState) return;
          structureState.loading = false;
          state.error = action.error.message;
        }
      })
      .addCase(fetchEmployeesList.pending, (state, action) => {
        state.employees.loading = true;
        state.employees.error = null;
      })
      .addCase(fetchEmployeesList.fulfilled, (state, action) => {
        state.employees.loading = false;
        state.employees.data = action.payload.response;
      })
      .addCase(fetchEmployeesList.rejected, (state, action) => {
        state.employees.loading = false;
        state.employees.error = action.error.message;
      });
  },
});

export const {
  fetchDataSuccess,
  appendListData,
  mergeListData,
  resetListData,
  touchModuleAccess,
  evictInactiveModules,
  setLastDoc,
  setCurrentCollection,
  setLoading,
  setEmployees,
  setFacets,
  setError,
  setSelectedDate,
  setSelectedView,
  setSearchValue,
  setSortOrder,
  setSourceCurrency,
  setLastUpdate,
  setHasActiveFilters,
  setQuickFilters,
  setTaskScope,
  setActiveTab,
  addEasyAddItem,
  updateEasyAddItem,
  reorderEasyAddItems,
  toggleEasyAddItemComplete,
  setKanbanCollapsed,
  toggleSelectItem,
  selectAllItems,
  clearSelectedItems,
  removeListItems,
  clearList,
} = listSlice.actions;

export default listSlice.reducer;
